
import Anchor from "../../../../../shared/lib/html5-software-library/src/ranj/layout/Anchor";
import LayoutEvent from "../../../../../shared/lib/html5-software-library/src/ranj/layout/LayoutEvent";
import AnimationFactory from "../../../../../shared/lib/html5-software-library/src/ranj/ui/animation/AnimationFactory";
import AnimationOptions from "../../../../../shared/lib/html5-software-library/src/ranj/ui/animation/AnimationOptions";
import { isNull } from "../../../../../shared/lib/html5-software-library/src/ranj/util/TypeUtil";
import { getURLParameter } from "../../../../../shared/lib/html5-software-library/src/ranj/util/URLUtil";
import SkinKeys from "../../../../../shared/src/ranj/app/ui/SkinKeys";
import GameConfig from "../model/GameConfig";
import GameModel from "../model/GameModel";
import GameStatus from "../model/GameStatus";
import Navigation from "../model/Navigation";
import SaveModel from "../model/SaveModel";
import AbstractAccuracyScreenMediator from "./AbstractAccuracyScreenMediator";
import BottomHudView from "./BottomHudView";
import GameElementSkinFactory from "./GameElementSkinFactory";
import HudView from "./HudView";
import PopupView from "./PopupView";
import PuzzleView from "./PuzzleView";

export default class GameScreenMediator extends AbstractAccuracyScreenMediator {

    /**
     * @param {*} target 
     * @param {*} dispatcher 
     * @param {*} languagePack 
     * @param {*} layoutManager 
     * @param {*} soundManager 
     * @param {GameModel} gameModel 
     * @param {GameConfig} gameConfig 
     * @param {RoadBuilder} roadBuilder 
     * @param {Object} roadAssets 
     * @param {SaveModel} saveModel 
     */
    constructor(target, dispatcher, languagePack, layoutManager, soundManager, gameModel, gameConfig, saveModel, gameAssets, gameElementSkinFactory) {
        super(target, dispatcher, languagePack, layoutManager, soundManager, gameModel, saveModel, gameAssets);
        this._gameConfig = gameConfig;
        this._elementFactory = gameElementSkinFactory;

        this._handleTick = this._handleTick.bind(this);
        this._handleGameModelStatus = this._handleGameModelStatus.bind(this);
        this._handleGameModelPuzzle = this._handleGameModelPuzzle.bind(this);
        this._handleGameModelScore = this._handleGameModelScore.bind(this);
        this._handleGameModelMistakes = this._handleGameModelMistakes.bind(this);
        this._handleGameModelElapsedTime = this._handleGameModelElapsedTime.bind(this);
        this._handleGameModelMatch = this._handleGameModelMatch.bind(this);
        this._handleGameModelMismatch = this._handleGameModelMismatch.bind(this);
        this._handlePuzzleViewItemSelect = this._handlePuzzleViewItemSelect.bind(this);
        this._handleLayoutChanged = this._handleLayoutChanged.bind(this);
    }

    initialize() {
        super.initialize();

        this.anchorObject(this._target.hud, Anchor.T);
        this.anchorObject(this._target.hudBottom, Anchor.B);
        this.anchorObject(this._target.level, Anchor.TL);

        this._levelPlaceholder = this._target.level;
        this._levelPlaceholder.removeAllChildren();

        this._hud = new HudView(this._target.hud, this._languagePack);
        this._hudBottom = new BottomHudView(this._target.hudBottom, this._languagePack);

        this._hudBottom.addEventListener(BottomHudView.SUBMIT, this._handleSubmitClick.bind(this));
        this._hudBottom.enableSubmit(false);

        this._initializeDebug();

        this._addEventListeners();

        this._hud.setScore(0);
        this._hud.setMistakes(0);
        this._hud.setTime(this._gameConfig.time * 1000);

        const obj = this._saveModel.getSaveGame();
        this._restored = !isNull(obj);
        if (this._restored) {
            this._restoreGame(obj);
        } else {
            this._startTutorial();
        }
    }

    destroy() {
        super.destroy();
        this._removeEventListeners();
        this._hud.destroy();
        this._hudBottom.destroy();
        this._destroyPuzzleView(this._puzzleView);
        if (window.gamescreen == this) {
            window.gamescreen = null;
        }
    }

    _addEventListeners() {
        this._gameModel.addEventListener(GameModel.STATUS, this._handleGameModelStatus);
        this._gameModel.addEventListener(GameModel.PUZZLE, this._handleGameModelPuzzle);
        this._gameModel.addEventListener(GameModel.SCORE, this._handleGameModelScore);
        this._gameModel.addEventListener(GameModel.MISTAKES, this._handleGameModelMistakes);
        this._gameModel.addEventListener(GameModel.ELAPSED_TIME, this._handleGameModelElapsedTime);
        this._gameModel.addEventListener(GameModel.MATCH, this._handleGameModelMatch);
        this._gameModel.addEventListener(GameModel.MISMATCH, this._handleGameModelMismatch);
        this.registerEventListener(LayoutEvent.CHANGED, this._handleLayoutChanged);
    }
    _removeEventListeners() {
        this._gameModel.removeEventListener(GameModel.STATUS, this._handleGameModelStatus);
        this._gameModel.removeEventListener(GameModel.PUZZLE, this._handleGameModelPuzzle);
        this._gameModel.removeEventListener(GameModel.SCORE, this._handleGameModelScore);
        this._gameModel.removeEventListener(GameModel.MISTAKES, this._handleGameModelMistakes);
        this._gameModel.removeEventListener(GameModel.ELAPSED_TIME, this._handleGameModelElapsedTime);
        this._gameModel.removeEventListener(GameModel.MATCH, this._handleGameModelMatch);
        this._gameModel.removeEventListener(GameModel.MISMATCH, this._handleGameModelMismatch);
    }

    _handleLayoutChanged() {
        this._updateViewport();
    }

    _getViewport() {
        const viewport = this._layoutManager.getViewport(this._target);//in stage coordspace     
        //Correct for position of puzzle view and space covered by huds   
        this._target.localToLocalRect(viewport, this._levelPlaceholder, viewport);
        viewport.y = 0;
        viewport.height -= 348;//Hardcoded...
        return viewport;
    }

    _updateViewport() {
        if (this._puzzleView) {
            this._puzzleView.viewport = this._getViewport();
        }
    }

    _handleGameModelScore() {
        this._hud.setScore(this._gameModel.score);
    }
    _handleGameModelMistakes() {
        this._hud.setMistakes(this._gameModel.mistakeCount);
    }

    _handleGameModelStatus({ data: status }) {
        switch (status) {
            case GameStatus.TIMEOUT:
                this._stopTicker();
                this.showPopupForPrefix("game.timeout", PopupView.TIMEOUT, _ => this.gotoScreen(SkinKeys.END_SCREEN));
                break;
        }
    }

    _handleGameModelElapsedTime() {
        this._hud.setTime(this._gameModel.remainingTime);
    }

    _startTicker() {
        if (!this._tickerStarted) {
            this._tickTime = performance.now();
            this._target.stage.addEventListener("tick", this._handleTick);
            this._tickerStarted = true;
        }
    }
    _stopTicker() {
        this._tickerStarted = false;
        this._target.stage.removeEventListener("tick", this._handleTick);
    }
    _handleTick() {
        if (!this._paused) {
            const now = performance.now();
            const delta = now - this._tickTime;
            this._tickTime = now;
            this._gameModel.step(delta);
        }
    }

    // ENTRY POINTS 

    _startPractice() {
        this._hud.showPracticeTime();
        this._hud.showPracticeIndicator(true);
        this._gameModel.startPractice();
    }

    _startGame() {
        this._hud.showPracticeIndicator(false);
        this._gameModel.start();
        //Force model to update some values
        this._gameModel.step(0);
        //some seconds delay for level transition which can be laggy on low-performing devices
        this.callDelayed(this._startTicker.bind(this), 3000);
    }

    _restoreGame(savegame) {
        this._gameModel.restore(savegame);
        const delay = this.checkSavegameRestoredFeedback(500) ? 1500 : 1000;
        this.callDelayed(this._startTicker.bind(this), delay);
    }

    //

    _doNextPuzzle() {
        this._gameModel.doNextPuzzle();
    }

    _handleGameModelPuzzle() {
        this._doPuzzleTransition();
    }

    _doPuzzleTransition() {
        const cross = this._puzzleView != null;
        const puzzleModel = this._gameModel.puzzle;
        const sampleSkin = this._elementFactory.getItem(puzzleModel.target);
        this._hudBottom.enableSubmit(false);
        const createNew = _ => {
            const target = this._levelPlaceholder.addChild(new createjs.Container());
            this._puzzleView = new PuzzleView(target, puzzleModel, this._elementFactory, this._getViewport());
            this._puzzleView.enabled = false;
            target.alpha = 0;
        }
        const animateIn = delay => {
            this.callDelayed(_ => {
                this._hudBottom.showFinishPractice(this._gameModel.status == GameStatus.PRACTICE && !this._gameModel.hasNextPracticePuzzle());
            }, 450);
            this._puzzleView.animateIn(300, delay, onAnimateInComplete);
        }
        const onAnimateInComplete = _ => {
            this._puzzleView.addEventListener(PuzzleView.SELECT, this._handlePuzzleViewItemSelect);
            this._puzzleView.enabled = this._gameModel.status != GameStatus.TUTORIAL;
            this._hudBottom.enableSubmit(this._gameModel.status != GameStatus.TUTORIAL);
            if (this._gameModel.status == GameStatus.PRACTICE && this._gameModel.puzzleIndex == 0) {
                this.callDelayed(_ => { this._showDragNudge(); }, 300);
            }
        }
        if (cross) {
            this._puzzleView.enabled = false;
            this._puzzleView.animateOut(300, 0, _ => {
                this._destroyPuzzleView(this._puzzleView);
                this.callDelayed(createNew, 50);
                this.callDelayed(_ => animateIn(700), 150);
            });
            this.callDelayed(_ => {
                this._hudBottom.crossFadeSample(sampleSkin, puzzleModel.flipped, 400, 300);
            }, 400);
        } else {
            createNew();
            animateIn(0);
            this._hudBottom.setSample(sampleSkin, puzzleModel.flipped);
        }
    }

    _showDragNudge() {
        const dragNudgeSkin = new this._gameAssets.DragNudgeSkin();
        this._puzzleView.doDragNudge(dragNudgeSkin);
    }

    /**
     * @param {PuzzleView} puzzleView 
     */
    _destroyPuzzleView(puzzleView = null) {
        if (puzzleView) {
            this._levelPlaceholder.removeChild(puzzleView.target);
            puzzleView.destroy();
        }
    }


    /*******************************
     *     PUZZLE INTERACTION
     *******************************/

    _handleSubmitClick() {
        this._submitPuzzle();
    }

    _submitPuzzle() {
        if (this._gameModel.status == GameStatus.PRACTICE) {
            if (this._gameModel.hasNextPracticePuzzle()) {
                this._doNextPuzzle();
            } else {
                this._startGateKeeper();
            }
        } else {
            this._gameModel.submitPuzzle();
            this._doNextPuzzle();
        }
    }

    _handlePuzzleViewItemSelect({ data: slotIndex }) {
        //in case this is during the drag nudge (timer not running yet), start the ticker
        this._startTicker();
        this._gameModel.selectItem(slotIndex);
    }

    _handleGameModelMatch({ data: slotIndex }) {
        this._puzzleView.doMatch(slotIndex);

    }
    _handleGameModelMismatch({ data: slotIndex }) {
        this._puzzleView.doMismatch(slotIndex);
    }

    /********************
     *     GENERIC
     ********************/

    showPopupForPrefix(prefix, type, callbackOk, callbackCancel) {
        const title = this.getText(`${prefix}.title`);
        const body = this.getText(`${prefix}.body`);
        const buttonParams = [];
        const ok = this.getText(`${prefix}.ok`);
        if (ok) {
            buttonParams.push({ label: ok, callback: callbackOk });
        }
        const cancel = this.getText(`${prefix}.cancel`);
        if (cancel) {
            buttonParams.push({ label: cancel, callback: callbackCancel });
        }

        this.showPopup(title, body, buttonParams, type);
    }


    /********************
     *     TUTORIAL
     ********************/

    _startTutorial(again = false) {
        if (getURLParameter("skiptutorial") != "1") {
            this._gameModel.startTutorial();
            this._puzzleView.enabled = false;
            const delay = again ? 0 : this._gameConfig.getValue("tutorial.delay");
            const duration = this._gameConfig.getValue("tutorial.fadeDuration");
            const callback = _ => {
                this._hud.setScore(17);
                this._hud.setMistakes(6);
                this._hud.showPracticeIndicator(true);
                this._hud.setTime(this._gameConfig.time * 1000);
                this._hudBottom.enableSubmit(true);
            }
            const options = new AnimationOptions(delay, duration, createjs.Ease.quadIn, [callback]);
            this.openPopup(SkinKeys.TUTORIAL_OVERLAY, this._handleTutorialClose.bind(this), AnimationFactory.FADE_IN, options);
        } else {
            this._handleTutorialClose();
        }
    }

    _handleTutorialClose() {
        this._hudBottom.enableSubmit(false);
        if (getURLParameter("skippractice") != "1") {
            this.callDelayed(this._startPractice.bind(this), 1000);
        } else {
            this.callDelayed(this._startGame.bind(this), 1000);
        }
    }

    _startGateKeeper() {
        const delay = this._gameConfig.getValue("gatekeeper.delay");
        const duration = this._gameConfig.getValue("gatekeeper.fadeDuration");
        const callback = _ => {
            this._puzzleView.enabled = false;
            this._hud.showPracticeIndicator(false);
            this._hudBottom.showFinishPractice(false);
            this._hud.setTime(this._gameConfig.time * 1000);
        }
        const options = new AnimationOptions(delay, duration, createjs.Ease.quadIn, [callback]);
        this.openPopup(SkinKeys.GATEKEEPER_OVERLAY, this._handleGateKeeperClose.bind(this), AnimationFactory.FADE_IN, options);
    }

    _handleGateKeeperClose(navType) {
        this._hudBottom.enableSubmit(false);
        if (navType == Navigation.BACK) {
            this._startTutorial();
        } else {
            this.callDelayed(this._startGame.bind(this), 1000);
        }
    }


    /********************
     *     DEBUG
     ********************/

    _initializeDebug() {
        if (ranj.DEBUG) {
            window.document.addEventListener('keyup', e => {
                const c = String.fromCharCode(e.which);
                //console.log(c, e.code, e.which);
                if (c == "P") {
                    this._paused = !this._paused;
                    if (!this._paused) {
                        this._tickTime = performance.now();
                    }
                } else if (c == "E") {
                    this.gotoScreen(SkinKeys.END_SCREEN);
                } else if (c == "L") {
                    this._gameModel._puzzleIndex = this._gameConfig.puzzles.length - 2;
                    this._gameModel.doNextPuzzle();
                } else if (c == "F") {
                    this._gameModel._puzzleIndex = this._gameConfig.puzzles.length - 1;
                    this._gameModel.doNextPuzzle();
                } else if (c == "T") {
                    this._gameModel.step(this._gameModel.remainingTime - 1000);
                } else if (c == "C") { //reset puzzle
                    this._gameModel._puzzleIndex--;
                    this._doNextPuzzle();
                } else if (c == "D") {
                    this._showDragNudge();
                } else if (c == "1") {
                    this._gameModel._puzzleIndex = -1;
                    this._doNextPuzzle();
                } else if (e.which == 37) {
                    this._gameModel._puzzleIndex = Math.max(-1, this._gameModel._puzzleIndex - 2);
                    this._doNextPuzzle();
                } else if (e.which == 39) {
                    this._gameModel.doNextPuzzle();
                }
            });
            window.gamescreen = this;//for console access
        }
    }

}
